import { useState } from "react";
import { AlertTriangle, Calendar as CalendarIcon, Plus, Minus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TERMINAL_ID } from "@/constants/EngineConstants";
import { INSURANCE_BASE_URL, INSURANCE_DESTINATIONS } from "@/constants/InsuranceConstants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface InsurancePassenger {
  age: string;
  error?: string;
}

interface InsuranceErrors {
  destination?: string;
  departureDate?: string;
  returnDate?: string;
  passengers?: string;
  terminalId?: string;
}

const DESTINATION_PLACEHOLDER_ID = "0";

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const getDateOnly = (date: Date) => {
  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);
  return dateOnly;
};

const isValidDate = (date: Date | undefined): date is Date => {
  return date instanceof Date && !Number.isNaN(date.getTime());
};

const isPastDate = (date: Date) => getDateOnly(date) < getToday();

const isBeforeDate = (date: Date, minDate: Date) => getDateOnly(date) < getDateOnly(minDate);

const validatePassengerAge = (ageValue: string) => {
  if (!ageValue) {
    return "La edad es obligatoria";
  }

  const age = Number(ageValue);

  if (!Number.isInteger(age) || age < 0 || age > 120) {
    return "Edad inválida";
  }

  return undefined;
};

const encodePathPart = (value: string | number) => encodeURIComponent(String(value));

const getTerminalId = () => TERMINAL_ID?.trim();

const EngineAssistance = () => {
    const [assistanceDestination, setAssistanceDestination] = useState<string>(DESTINATION_PLACEHOLDER_ID);
    const [assistanceDepartureDate, setAssistanceDepartureDate] = useState<Date | undefined>(undefined);
    const [assistanceReturnDate, setAssistanceReturnDate] = useState<Date | undefined>(undefined);
    const [assistancePassengers, setAssistancePassengers] = useState<InsurancePassenger[]>([{ age: "", error: undefined }]);
    const [assistanceDepartureDateOpen, setAssistanceDepartureDateOpen] = useState(false);
    const [assistanceReturnDateOpen, setAssistanceReturnDateOpen] = useState(false);
    const [assistancePassengersOpen, setAssistancePassengersOpen] = useState(false);
    const [errors, setErrors] = useState<InsuranceErrors>({});

    const selectedDestination = INSURANCE_DESTINATIONS.find(
        (destination) => String(destination.id) === assistanceDestination
    );
    const showCubaAlert = selectedDestination?.id === 5;

    const getAssistancePassengerText = () => {
        const count = assistancePassengers.length;
        return count === 1 ? "1 pasajero" : `${count} pasajeros`;
    };

    const handleAddAssistancePassenger = () => {
        setAssistancePassengers(prev => [...prev, { age: "", error: undefined }]);
        if (errors.passengers) {
            setErrors(prev => ({ ...prev, passengers: undefined }));
        }
    };

    const handleRemoveAssistancePassenger = (index: number) => {
        if (assistancePassengers.length <= 1) return;
        setAssistancePassengers(prev => prev.filter((_, i) => i !== index));
    };

    const handleAssistancePassengerAgeChange = (index: number, value: string) => {
        const numericValue = value.replace(/\D/g, "");
        setAssistancePassengers(prev =>
            prev.map((p, i) => {
                if (i !== index) return p;
                const error = validatePassengerAge(numericValue);
                return { age: numericValue, error };
            })
        );
        if (errors.passengers) {
            setErrors(prev => ({ ...prev, passengers: undefined }));
        }
    };

    const validateAssistancePassengerAge = (index: number) => {
        setAssistancePassengers(prev =>
            prev.map((p, i) => {
                if (i !== index) return p;
                return { ...p, error: validatePassengerAge(p.age) };
            })
        );
    };

    const formatDateForUrl = (date: Date) => format(date, "yyyy-MM-dd");

    const validateForm = () => {
        const newErrors: InsuranceErrors = {};
        const destination = INSURANCE_DESTINATIONS.find(
            (item) => String(item.id) === assistanceDestination
        );
        const terminalId = getTerminalId();
        const passengersWithErrors = assistancePassengers.map((passenger) => ({
            ...passenger,
            error: validatePassengerAge(passenger.age),
        }));
        const hasPassengerErrors = passengersWithErrors.some((passenger) => passenger.error);

        if (!destination || destination.id === 0) {
            newErrors.destination = "Selecciona un destino";
        }

        if (!isValidDate(assistanceDepartureDate)) {
            newErrors.departureDate = "La fecha de ida es obligatoria";
        } else if (isPastDate(assistanceDepartureDate)) {
            newErrors.departureDate = "La fecha de ida no puede ser anterior a hoy";
        }

        if (!isValidDate(assistanceReturnDate)) {
            newErrors.returnDate = "La fecha de vuelta es obligatoria";
        } else if (isPastDate(assistanceReturnDate)) {
            newErrors.returnDate = "La fecha de vuelta no puede ser anterior a hoy";
        } else if (isValidDate(assistanceDepartureDate) && isBeforeDate(assistanceReturnDate, assistanceDepartureDate)) {
            newErrors.returnDate = "La fecha de vuelta no puede ser anterior a la fecha de ida";
        }

        if (assistancePassengers.length < 1) {
            newErrors.passengers = "Agrega al menos un pasajero";
        } else if (hasPassengerErrors) {
            newErrors.passengers = "Revisa las edades de los pasajeros";
        }

        if (!terminalId) {
            newErrors.terminalId = "No se pudo obtener el terminalId";
        }

        setAssistancePassengers(passengersWithErrors);
        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleAssistance = () => {
        if (!validateForm()) {
            return;
        }

        const destination = selectedDestination;
        const terminalId = getTerminalId();

        if (!destination || !assistanceDepartureDate || !assistanceReturnDate || !terminalId) {
            return;
        }

        const passengerAges = assistancePassengers.map((passenger) => passenger.age).join(",");
        const searchPath = [
            destination.value,
            destination.id,
            formatDateForUrl(assistanceDepartureDate),
            formatDateForUrl(assistanceReturnDate),
        ].map(encodePathPart).join("/");

        const searchPathWithPassengers = `${searchPath}/${passengerAges}`;

        const queryParams = new URLSearchParams();
        queryParams.append("terminalId", terminalId);

        const finalUrl = `${INSURANCE_BASE_URL}/${searchPathWithPassengers}?${queryParams.toString()}`;

        window.open(finalUrl, "_blank");
    };

    return (
    <>
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <h2 className="text-xl lg:text-2xl font-semibold text-foreground">
                Asistencia en viaje
            </h2>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
            {/* Destino */}
            <div className="relative">
                <label className="block text-sm font-semibold text-foreground mb-2">Destino</label>
                <Select
                    value={assistanceDestination}
                    onValueChange={(value) => {
                        setAssistanceDestination(value);
                        if (errors.destination) {
                            setErrors(prev => ({ ...prev, destination: undefined }));
                        }
                    }}
                >
                <SelectTrigger
                    className={cn(
                        "w-full h-12 border-2 focus:border-secondary",
                        errors.destination ? "border-destructive" : "border-border"
                    )}
                >
                    <SelectValue placeholder="Selecciona destino" />
                </SelectTrigger>
                <SelectContent className="w-[min(90vw,240px)] bg-background border border-border">
                    {INSURANCE_DESTINATIONS.map((dest) => (
                    <SelectItem key={dest.id} value={dest.id.toString()} disabled={dest.id === 0}>
                        {dest.label}
                    </SelectItem>
                    ))}
                </SelectContent>
                </Select>
                <div className="md:min-h-5 mt-1">
                    {errors.destination && (
                        <span className="text-destructive text-xs">{errors.destination}</span>
                    )}
                </div>
            </div>

            {/* Fecha Ida */}
            <div className="relative">
                <label className="block text-sm font-semibold text-foreground mb-2">Fecha de ida</label>
                <Popover open={assistanceDepartureDateOpen} onOpenChange={setAssistanceDepartureDateOpen}>
                <PopoverTrigger asChild>
                    <button
                    className={cn(
                        "relative w-full h-12 px-4 pl-10 border-2 rounded-lg focus:border-secondary focus:outline-none transition-colors bg-background text-left",
                        errors.departureDate ? "border-destructive" : "border-border",
                        assistanceDepartureDate ? "text-foreground" : "text-muted-foreground"
                    )}
                    >
                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    {assistanceDepartureDate 
                        ? format(assistanceDepartureDate, "dd MMM yyyy", { locale: es }) 
                        : "Selecciona fecha"}
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                    mode="single"
                    selected={assistanceDepartureDate}
                    onSelect={(date) => {
                        setAssistanceDepartureDate(date);
                        setAssistanceDepartureDateOpen(false);
                        if (errors.departureDate) {
                            setErrors(prev => ({ ...prev, departureDate: undefined }));
                        }
                        if (date && assistanceReturnDate && assistanceReturnDate < date) {
                            setAssistanceReturnDate(undefined);
                            setErrors(prev => ({
                                ...prev,
                                returnDate: "La fecha de vuelta no puede ser anterior a la fecha de ida",
                            }));
                        }
                    }}
                    disabled={(date) => {
                        const today = new Date(new Date().setHours(0, 0, 0, 0));
                        return date < today;
                    }}
                    initialFocus
                    locale={es}
                    className="p-3 pointer-events-auto"
                    />
                </PopoverContent>
                </Popover>
                <div className="md:min-h-5 mt-1">
                    {errors.departureDate && (
                        <span className="text-destructive text-xs">{errors.departureDate}</span>
                    )}
                </div>
            </div>

            {/* Fecha Vuelta */}
            <div className="relative">
                <label className="block text-sm font-semibold text-foreground mb-2">Fecha de vuelta</label>
                <Popover open={assistanceReturnDateOpen} onOpenChange={setAssistanceReturnDateOpen}>
                <PopoverTrigger asChild>
                    <button
                    className={cn(
                        "relative w-full h-12 px-4 pl-10 border-2 rounded-lg focus:border-secondary focus:outline-none transition-colors bg-background text-left",
                        errors.returnDate ? "border-destructive" : "border-border",
                        assistanceReturnDate ? "text-foreground" : "text-muted-foreground"
                    )}
                    >
                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    {assistanceReturnDate 
                        ? format(assistanceReturnDate, "dd MMM yyyy", { locale: es }) 
                        : "Selecciona fecha"}
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                    mode="single"
                    selected={assistanceReturnDate}
                    onSelect={(date) => {
                        setAssistanceReturnDate(date);
                        setAssistanceReturnDateOpen(false);
                        if (errors.returnDate) {
                            setErrors(prev => ({ ...prev, returnDate: undefined }));
                        }
                    }}
                    disabled={(date) => {
                        const today = new Date(new Date().setHours(0, 0, 0, 0));
                        const minDate = assistanceDepartureDate || today;
                        return date < minDate;
                    }}
                    initialFocus
                    locale={es}
                    className="p-3 pointer-events-auto"
                    />
                </PopoverContent>
                </Popover>
                <div className="md:min-h-5 mt-1">
                    {errors.returnDate && (
                        <span className="text-destructive text-xs">{errors.returnDate}</span>
                    )}
                </div>
            </div>

            {/* Pasajeros - Selector compacto con Popover */}
            <div className="relative">
                <label className="block text-sm font-semibold text-foreground mb-2">Pasajeros</label>
                <Popover open={assistancePassengersOpen} onOpenChange={setAssistancePassengersOpen}>
                <PopoverTrigger asChild>
                    <button
                    className={cn(
                        "relative w-full h-12 px-4 pl-10 border-2 rounded-lg focus:border-secondary focus:outline-none transition-colors bg-background text-left",
                        errors.passengers ? "border-destructive" : "border-border",
                        "text-foreground"
                    )}
                    >
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    {getAssistancePassengerText()}
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-4 bg-background border border-border z-50" align="start">
                    <div className="space-y-3">
                    {assistancePassengers.map((passenger, index) => (
                        <div key={index}>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-foreground whitespace-nowrap">
                            Edad #{index + 1}
                            </span>
                            <input
                            type="text"
                            inputMode="numeric"
                            value={passenger.age}
                            onChange={(e) => handleAssistancePassengerAgeChange(index, e.target.value)}
                            onBlur={() => validateAssistancePassengerAge(index)}
                            placeholder=""
                            style={{ maxWidth: '60px' }}
                            className={cn(
                                "flex-1 h-10 px-3 border-2 rounded-lg focus:border-secondary focus:outline-none transition-colors bg-background text-foreground text-center",
                                passenger.error ? "border-destructive" : "border-border"
                            )}
                            />
                            <button
                            type="button"
                            onClick={() => handleRemoveAssistancePassenger(index)}
                            disabled={assistancePassengers.length <= 1}
                            className={cn(
                                "flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors flex-shrink-0",
                                assistancePassengers.length <= 1
                                ? "border-muted text-muted cursor-not-allowed opacity-50"
                                : "border-primary text-primary hover:bg-primary/25"
                            )}
                            title="Eliminar pasajero"
                            >
                            <Minus className="w-3 h-3" />
                            </button>
                        </div>
                        {passenger.error && (
                            <span className="text-destructive text-xs mt-1 block">{passenger.error}</span>
                        )}
                        </div>
                    ))}
                    </div>
                    <button
                    type="button"
                    onClick={handleAddAssistancePassenger}
                    className="flex items-center mt-2 text-secondary font-medium hover:text-primary px-0 py-2 transition-colors w-full"
                    >
                    <Plus className="w-4 h-4" />
                    Agregar pasajero
                    </button>
                </PopoverContent>
                </Popover>
                <div className="md:min-h-5 mt-1">
                    {errors.passengers && (
                        <span className="text-destructive text-xs">{errors.passengers}</span>
                    )}
                </div>
            </div>
        </div>

        {/* Search Button */}
        {errors.terminalId && (
            <span className="block text-destructive text-xs mb-3">{errors.terminalId}</span>
        )}
        <Button onClick={handleAssistance} className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold py-6 px-8 rounded-full text-base shadow-glow">
            Cotizar
        </Button>
        {showCubaAlert && (
            <Alert className="mt-3 flex items-center gap-2 py-2 border-secondary/40 bg-secondary/10 text-foreground [&>svg]:static [&>svg]:left-auto [&>svg]:top-auto [&>svg]:translate-y-0 [&>svg~*]:pl-0 [&>svg+div]:translate-y-0">
                <AlertTriangle className="h-4 w-4 text-secondary" />
                <AlertDescription>No aplica para destino Cuba</AlertDescription>
            </Alert>
        )}
    </>
    )
}

export default EngineAssistance;
